import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Smartphone, Shield, Heart } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function LastNed() {
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <LanguageSwitcher currentLang={i18n.language} onChangeLang={changeLanguage} />
            <Link to="/" className="p-1 border-2 border-black rounded-md hover:bg-black/5 transition-colors">
              <img 
                src="https://customer-assets.emergentagent.com/job_mindairy-site/artifacts/6hwqvtsj_mindary%20logo.jpeg" 
                alt="mindary logo" 
                className="w-10 h-10 object-contain"
              />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-emerald-50 via-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-500 mb-6">
              <Smartphone className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold text-foreground tracking-tight mb-4" data-testid="download-title">
              {t('download.title')}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('download.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Download Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
              {t('download.availableOn')}
            </h2>
            <p className="text-muted-foreground">
              {t('download.downloadDesc')}
            </p>
          </motion.div>

          {/* App Store Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-6 justify-center mb-16"
          >
            {/* App Store */}
            <a 
              href="https://apps.apple.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group"
              data-testid="app-store-link"
            >
              <Card className="bg-black text-white hover:scale-105 transition-transform cursor-pointer">
                <CardContent className="flex items-center gap-4 p-4">
                  <svg className="w-10 h-10" viewBox="0 0 384 512" fill="currentColor">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-xs text-gray-300">{t('download.downloadFrom')}</p>
                    <p className="text-xl font-semibold">{t('download.appStore')}</p>
                  </div>
                </CardContent>
              </Card>
            </a>

            {/* Google Play */}
            <a 
              href="https://play.google.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group"
              data-testid="google-play-link"
            >
              <Card className="bg-black text-white hover:scale-105 transition-transform cursor-pointer">
                <CardContent className="flex items-center gap-4 p-4">
                  <svg className="w-10 h-10" viewBox="0 0 512 512" fill="currentColor">
                    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-xs text-gray-300">{t('download.availableAt')}</p>
                    <p className="text-xl font-semibold">{t('download.googlePlay')}</p>
                  </div>
                </CardContent>
              </Card>
            </a>
          </motion.div>

          {/* Features */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto"
          >
            <div className="text-center p-6 bg-blue-50 rounded-2xl">
              <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">{t('download.safePrivate')}</h3>
              <p className="text-sm text-muted-foreground">{t('download.safePrivateDesc')}</p>
            </div>
            <div className="text-center p-6 bg-amber-50 rounded-2xl">
              <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">{t('download.withCare')}</h3>
              <p className="text-sm text-muted-foreground">{t('download.withCareDesc')}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
